import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

export default function JoinThankYou() {
  const [sp] = useSearchParams();
  const session_id = sp.get("session_id");
  const { user, authLoading, refreshMembership } = useApp();
  const [st, setSt] = useState({ state: "polling" });
  const attempts = useRef(0);

  useEffect(() => {
    if (authLoading) return;
    if (!session_id || !user) { setSt({ state: "error" }); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/membership/status/${session_id}`);
        const s = data.subscription?.status;
        if (["active", "trialing"].includes(s)) { await refreshMembership?.(); setSt({ state: "done", sub: data.subscription }); return; }
        if (s === "expired") { setSt({ state: "failed" }); return; }
        if (attempts.current >= 12) { setSt({ state: "timeout" }); return; }
        timer = setTimeout(poll, 2000);
      } catch { setSt({ state: "error" }); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [session_id, user, authLoading, refreshMembership]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid={`join-thankyou-${st.state}`}>
      <div className="rounded-3xl border p-8 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        {st.state === "polling" && (<><Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--burgundy)" }} /><div className="eyebrow mb-2">Confirming your membership</div><p className="text-sm" style={{ color: "var(--text-muted)" }}>One moment while we confirm with Stripe.</p></>)}
        {st.state === "done" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--burgundy)" }} />
            <div className="eyebrow mb-2">Welcome to the Learning Circle</div>
            <h1 className="font-serif text-3xl sm:text-4xl mb-3" style={{ color: "var(--burgundy)" }}>The full curriculum is now open to you.</h1>
            <p className="text-sm mb-6" style={{ color: "var(--text-obsidian)" }} data-testid="join-thankyou-detail">
              Your Learning Circle membership is <strong>active</strong>.{st.sub?.current_period_end ? <> Your next billing date is {st.sub.current_period_end.slice(0, 10)}.</> : null} You may cancel at any time from My Temple; access continues through the end of the current billing period.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/learn" data-testid="join-thankyou-learn-btn" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Enter the Learning Circle <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/my-temple" data-testid="join-thankyou-mytemple-btn" className="rounded-full px-6 py-3 text-sm font-medium border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Open My Temple</Link>
            </div>
          </>
        )}
        {["failed", "timeout", "error"].includes(st.state) && (
          <>
            <AlertTriangle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--rose-dark)" }} />
            <h2 className="font-serif text-2xl mb-3" style={{ color: "var(--burgundy)" }}>{st.state === "failed" ? "This checkout was not completed." : "We couldn't confirm this yet."}</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>{!user && !authLoading ? "Please sign in with the account you used at checkout." : "If your payment completed, your membership will activate once Stripe confirms it. You have not been charged twice."}</p>
            <Link to={user ? "/my-temple" : "/login?next=/my-temple"} className="rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>{user ? "Open My Temple" : "Sign in"}</Link>
          </>
        )}
      </div>
    </div>
  );
}
